import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Pencil, Plus, RefreshCw, Save, Search, X } from 'lucide-react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { calculatePurchaseMarginPreview, findPurchasePreviewInventory } from '../../utils/purchaseMarginPreview';
import MarketChangesConfigSection from './MarketChangesConfigSection';
import BulkMarginImport from './BulkMarginImport';

const apiUrl = (path) => `${API_BASE_URL}${path}`;

const ledgerTypes = ['ASSET', 'LIABILITY', 'INCOME', 'EXPENSE', 'EQUITY'];
const stages = ['PURCHASE', 'INVENTORY', 'CHANNEL', 'OVERHEAD', 'SALE'];
const allocationMethods = [
  'BY_QUANTITY',
  'BY_WEIGHT',
  'BY_VALUE',
  'BY_VOLUME',
  'FIXED_PER_UNIT',
  'FIXED_PER_PACK',
  'MANUAL_PERCENTAGE',
  'EQUAL_SPLIT',
];

const emptyLedger = {
  ledger_code: '',
  ledger_name: '',
  ledger_type: 'EXPENSE',
  opening_balance: 0,
  is_active: true,
};

const emptyCostHead = {
  cost_head_code: '',
  cost_head_name: '',
  stage: 'PURCHASE',
  ledger_id: '',
  cost_type: 'EXPENSE',
  is_gst_applicable: false,
  is_active: true,
  notes: '',
};

const emptyAllocationRule = {
  cost_head_id: '',
  allocation_method: 'BY_VALUE',
  apply_level: 'PURCHASE_ITEM',
  priority: 100,
  is_active: true,
  notes: '',
};

const emptyPackLedger = {
  product_barcode_id: '',
  destination_type: 'all',
  dispatch_to_id: '',
  warehouse_id: '',
  sales_ledger_id: '',
  margin_percent: 0,
  proportion_factor: 1,
  discount_percent: 0,
  is_active: true,
  notes: '',
};

const configViews = [
  {
    key: 'overview',
    label: 'Overview',
    to: '/ecosystem/catalog/cost-ledger-config',
    title: 'Cost & Ledger Configurations',
    description: 'Configure ledgers, cost heads, allocation rules, and pack margins.',
  },
  {
    key: 'ledgers',
    label: 'Ledgers',
    to: '/ecosystem/catalog/cost-ledger-config/ledgers',
    title: 'Ledgers',
    description: 'Create and view accounting ledgers.',
  },
  {
    key: 'cost-heads',
    label: 'Cost Heads',
    to: '/ecosystem/catalog/cost-ledger-config/cost-heads',
    title: 'Cost Heads',
    description: 'Create and map purchase, inventory, and sales cost heads.',
  },
  {
    key: 'allocation-rules',
    label: 'Allocation Rules',
    to: '/ecosystem/catalog/cost-ledger-config/allocation-rules',
    title: 'Allocation Rules',
    description: 'Configure how cost heads are distributed across products.',
  },
  {
    key: 'product-pack-margins',
    label: 'Product Pack Margins',
    to: '/ecosystem/catalog/cost-ledger-config/product-pack-margins',
    title: 'Product Pack Margins',
    description: 'Configure margin, factor, discount, and sales ledger per pack.',
  },
  { key: 'market-changes', label: 'Market Changes', to: '/ecosystem/catalog/cost-ledger-config/market-changes', title: 'Market Changes', description: 'Configure market reference rates for dynamic packing and dispatch pricing.' },
];

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.products)) return value.products;
  return [];
};

const getBrandName = (row) =>
  row?.brand_name_english ||
  row?.brand_name_englishh ||
  row?.brand_name_telugu ||
  row?.brand_name ||
  row?.brand ||
  '';

const FormInput = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <input
      {...props}
      className="mt-1 h-10 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    />
  </label>
);

const FormSelect = ({ label, children, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <select
      {...props}
      className="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    >
      {children}
    </select>
  </label>
);

const ProductPackSearch = ({
  label,
  value,
  search,
  suggestions,
  onSearch,
  onSelect,
  selectedLabel,
}) => (
  <label className="relative block">
    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <input
      value={search}
      onChange={(event) => onSearch(event.target.value)}
      placeholder="Type product, MK barcode, vendor barcode, or pack"
      className="mt-1 h-10 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      required={!value}
    />
    {value ? (
      <div className="mt-1 text-xs font-semibold text-green-700">
        Selected: {selectedLabel}
      </div>
    ) : null}
    {search.trim() && !value ? (
      <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
        {suggestions.length ? (
          suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-blue-50"
            >
              <div className="font-semibold text-gray-900">{item.label}</div>
              <div className="text-xs text-gray-500">{item.searchHint}</div>
            </button>
          ))
        ) : (
          <div className="px-3 py-3 text-sm text-gray-500">
            No product pack found
          </div>
        )}
      </div>
    ) : null}
  </label>
);

const waitForNextPaint = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      resolve();
      return;
    }

    window.requestAnimationFrame(() => resolve());
  });

const ConfigTable = ({ title, rows, columns, emptyText, busy = false, busyText = 'Refreshing changes' }) => (
  <section className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-white shadow-sm">
    <div className="shrink-0 border-b px-4 py-3">
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
    </div>
    {busy ? (
      <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-[1px]">
        <div className="rounded-lg border bg-white px-5 py-4 text-center shadow-sm">
          <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
          <div className="text-sm font-bold text-gray-900">{busyText}</div>
        </div>
      </div>
    ) : null}
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-slate-100 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className="px-3 py-2 text-left">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row) => (
              <tr key={row.id} className="border-t">
                {columns.map((column) => (
                  <td key={column.key} className="px-3 py-2">
                    {column.render ? column.render(row) : row[column.key] || '-'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-500">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </section>
);

const CostLedgerConfigSection = ({ activeView = 'overview' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedView =
    configViews.find((view) => view.key === activeView) || configViews[0];
  const showOverview = selectedView.key === 'overview';
  const showLedgerForm = selectedView.key === 'ledgers';
  const showCostHeadForm = selectedView.key === 'cost-heads';
  const showAllocationRuleForm = selectedView.key === 'allocation-rules';
  const showProductPackMarginForm = selectedView.key === 'product-pack-margins';
  const hasConfigForm =
    showLedgerForm ||
    showCostHeadForm ||
    showAllocationRuleForm ||
    showProductPackMarginForm;
  const showLedgersTable = showOverview || selectedView.key === 'ledgers';
  const showCostHeadsTable = showOverview || selectedView.key === 'cost-heads';
  const showAllocationRulesTable =
    showOverview || selectedView.key === 'allocation-rules';
  const showProductPackMarginsTable =
    showOverview || selectedView.key === 'product-pack-margins';
  const token = useSelector((state) => state.posUser?.userInfo?.token || '');
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [ledgers, setLedgers] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [allocationRules, setAllocationRules] = useState([]);
  const [productPackLedgers, setProductPackLedgers] = useState([]);
  const [productBarcodes, setProductBarcodes] = useState([]);
  const [purchaseInventory, setPurchaseInventory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [ledgerForm, setLedgerForm] = useState(emptyLedger);
  const [costHeadForm, setCostHeadForm] = useState(emptyCostHead);
  const [ruleForm, setRuleForm] = useState(emptyAllocationRule);
  const [packLedgerForm, setPackLedgerForm] = useState(emptyPackLedger);
  const [editingPackLedgerId, setEditingPackLedgerId] = useState(null);
  const [packSearch, setPackSearch] = useState('');
  const [configSearch, setConfigSearch] = useState('');
  const [configSearchOpen, setConfigSearchOpen] = useState(false);
  const [previewBaseAmount, setPreviewBaseAmount] = useState('');

  const marginDestinations = useMemo(() => {
    const type = packLedgerForm.destination_type;
    if (type === 'internal_packing') {
      return [{ id: '', label: 'Internal Packing Dept' }];
    }
    if (type === 'warehouse') {
      return warehouses.map((row) => ({
        ...row,
        label: [row.warehouse_code, row.warehouse_name].filter(Boolean).join(' - ') || row.id,
      }));
    }
    if (type === 'outlet') {
      return outlets.map((row) => ({
        ...row,
        label: [row.outlet_code, row.outlet_name].filter(Boolean).join(' - ') || row.id,
      }));
    }
    if (type === 'vendor' || type === 'customer') {
      return stakeholders
        .filter((row) =>
          String(row.stakeholder_type || '').toLowerCase().includes(type)
        )
        .map((row) => ({
          ...row,
          label: [row.stackholder_code || row.stakeholder_code, row.stakeholder_name]
            .filter(Boolean)
            .join(' - ') || row.id,
        }));
    }
    return [];
  }, [packLedgerForm.destination_type, warehouses, outlets, stakeholders]);

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(apiUrl('/accounts/ensure'), {}, authConfig);
      const [
        ledgersRes,
        costHeadsRes,
        rulesRes,
        packLedgersRes,
        barcodesRes,
        warehousesRes,
        outletsRes,
        stakeholdersRes,
        inventoryRes,
      ] =
        await Promise.all([
          axios.get(apiUrl('/accounts/ledgers'), authConfig),
          axios.get(apiUrl('/accounts/cost-heads'), authConfig),
          axios.get(apiUrl('/accounts/allocation-rules'), authConfig),
          axios.get(apiUrl('/accounts/product-pack-ledgers'), authConfig),
          axios.get(apiUrl('/catalog-pg/product-barcodes'), authConfig),
          axios.get(apiUrl('/catalog-pg/warehouses'), authConfig),
          axios.get(apiUrl('/catalog-pg/outlets'), authConfig),
          axios.get(apiUrl('/catalog-pg/stakeholders'), authConfig),
          axios.get(apiUrl('/inventory-pg/products'), authConfig),
        ]);
      setLedgers(normalizeArray(ledgersRes.data));
      setCostHeads(normalizeArray(costHeadsRes.data));
      setAllocationRules(normalizeArray(rulesRes.data));
      setProductPackLedgers(normalizeArray(packLedgersRes.data));
      setProductBarcodes(normalizeArray(barcodesRes.data));
      setPurchaseInventory(normalizeArray(inventoryRes.data));
      setWarehouses(normalizeArray(warehousesRes.data));
      setOutlets(normalizeArray(outletsRes.data));
      setStakeholders(normalizeArray(stakeholdersRes.data));
      setLoadedOnce(true);
      return {
        ledgers: normalizeArray(ledgersRes.data),
        costHeads: normalizeArray(costHeadsRes.data),
        allocationRules: normalizeArray(rulesRes.data),
        productPackLedgers: normalizeArray(packLedgersRes.data),
        productBarcodes: normalizeArray(barcodesRes.data),
      };
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load configuration');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authConfig, token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveForm = async (event, path, payload, reset, successMessage) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await axios.post(apiUrl(path), payload, authConfig);
      reset();
      setMessage(successMessage);
      await loadAll();
      await waitForNextPaint();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const ledgerName = (id) =>
    ledgers.find((ledger) => String(ledger.id) === String(id))?.ledger_name || '-';

  const costHeadName = (id) =>
    costHeads.find((head) => String(head.id) === String(id))?.cost_head_name || '-';

  const destinationName = (row) => {
    if (!row.dispatch_to_id) return 'All';
    const source = row.destination_type === 'warehouse'
      ? warehouses
      : row.destination_type === 'outlet'
        ? outlets
        : stakeholders;
    const destination = source.find(
      (item) => String(item.id) === String(row.dispatch_to_id)
    );
    return destination?.warehouse_name || destination?.outlet_name ||
      destination?.stakeholder_name || destination?.stackholder_code ||
      `#${row.dispatch_to_id}`;
  };

  const makeBarcodeLabel = (row) => {
    if (!row) return '-';
    const productName = row.product_name_eng || row.product_name_tel || row.product_code;
    const brandName = getBrandName(row);
    return [
      brandName && productName && !String(productName).toLowerCase().startsWith(String(brandName).toLowerCase())
        ? `${brandName} ${productName}`
        : productName || brandName,
      row.quantity ? `${row.quantity} ${row.unit_short_code || row.unit_name || ''}` : '',
      row.mk_barcode ? `MK: ${row.mk_barcode}` : '',
    ].filter(Boolean).join(' - ');
  };

  const barcodeSearchText = (row) =>
    [
      makeBarcodeLabel(row),
      row.product_name_eng,
      row.product_name_tel,
      row.product_code,
      getBrandName(row),
      row.mk_barcode,
      row.barcode,
      row.hsncode,
    ].filter(Boolean).join(' ').toLowerCase();

  const barcodeLabel = (id) => {
    if (id === null || id === undefined || id === '*') return '*';
    const row = productBarcodes.find((item) => String(item.id) === String(id));
    return row ? makeBarcodeLabel(row) : id || '-';
  };

  const configSearchText = configSearch.trim().toLowerCase();

  const visibleSearchSources = useMemo(() => {
    const sources = [];

    if (showLedgersTable) {
      ledgers.forEach((ledger) => {
        const label = ledger.ledger_name || ledger.ledger_code || `Ledger #${ledger.id}`;
        sources.push({
          key: `ledger-${ledger.id}`,
          type: 'Ledger',
          label,
          value: label,
          text: [
            ledger.ledger_code,
            ledger.ledger_name,
            ledger.ledger_type,
          ].filter(Boolean).join(' ').toLowerCase(),
        });
      });
    }

    if (showCostHeadsTable) {
      costHeads.forEach((head) => {
        const label = head.cost_head_name || head.cost_head_code || `Cost Head #${head.id}`;
        sources.push({
          key: `cost-head-${head.id}`,
          type: 'Cost Head',
          label,
          value: label,
          text: [
            head.cost_head_code,
            head.cost_head_name,
            head.stage,
            ledgerName(head.ledger_id),
          ].filter(Boolean).join(' ').toLowerCase(),
        });
      });
    }

    if (showAllocationRulesTable) {
      allocationRules.forEach((rule) => {
        const label = costHeadName(rule.cost_head_id);
        sources.push({
          key: `allocation-rule-${rule.id}`,
          type: 'Allocation Rule',
          label,
          value: label,
          text: [
            label,
            rule.allocation_method,
            rule.apply_level,
            rule.priority,
            rule.notes,
          ].filter(Boolean).join(' ').toLowerCase(),
        });
      });
    }

    if (showProductPackMarginsTable) {
      productPackLedgers.forEach((row) => {
        const label = barcodeLabel(row.product_barcode_id);
        sources.push({
          key: `product-pack-margin-${row.id}`,
          type: 'Product Pack',
          label,
          value: label,
          text: [
            label,
            ledgerName(row.sales_ledger_id),
            row.margin_percent,
            row.proportion_factor,
            row.discount_percent,
            row.notes,
          ].filter(Boolean).join(' ').toLowerCase(),
        });
      });
    }

    return sources;
  }, [
    allocationRules,
    costHeads,
    ledgers,
    productPackLedgers,
    showAllocationRulesTable,
    showCostHeadsTable,
    showLedgersTable,
    showProductPackMarginsTable,
  ]);

  const searchPlaceholder = useMemo(() => {
    if (selectedView.key === 'ledgers') {
      return 'Search ledger name, code, or type';
    }

    if (selectedView.key === 'cost-heads') {
      return 'Search cost head name, code, stage, or ledger';
    }

    if (selectedView.key === 'allocation-rules') {
      return 'Search cost head, allocation method, level, or priority';
    }

    if (selectedView.key === 'product-pack-margins') {
      return 'Search product name, pack, MK barcode, margin, or sales ledger';
    }

    return 'Search ledgers, cost heads, allocation rules, or product packs';
  }, [selectedView.key]);

  const configSearchSuggestions = useMemo(() => {
    if (!configSearchText) return [];

    return visibleSearchSources
      .filter((source) => source.text.includes(configSearchText))
      .slice(0, 12);
  }, [configSearchText, visibleSearchSources]);

  const filterByConfigSearch = useCallback(
    (rows, getText) => {
      if (!configSearchText) return rows;

      return rows.filter((row) => getText(row).toLowerCase().includes(configSearchText));
    },
    [configSearchText]
  );

  const filteredLedgers = useMemo(
    () =>
      filterByConfigSearch(ledgers, (ledger) =>
        [ledger.ledger_code, ledger.ledger_name, ledger.ledger_type]
          .filter(Boolean)
          .join(' ')
      ),
    [filterByConfigSearch, ledgers]
  );

  const filteredCostHeads = useMemo(
    () =>
      filterByConfigSearch(costHeads, (head) =>
        [head.cost_head_code, head.cost_head_name, head.stage, ledgerName(head.ledger_id)]
          .filter(Boolean)
          .join(' ')
      ),
    [costHeads, filterByConfigSearch, ledgers]
  );

  const filteredAllocationRules = useMemo(
    () =>
      filterByConfigSearch(allocationRules, (rule) =>
        [
          costHeadName(rule.cost_head_id),
          rule.allocation_method,
          rule.apply_level,
          rule.priority,
          rule.notes,
        ]
          .filter(Boolean)
          .join(' ')
      ),
    [allocationRules, costHeads, filterByConfigSearch]
  );

  const filteredProductPackLedgers = useMemo(
    () =>
      filterByConfigSearch(productPackLedgers, (row) =>
        [
          barcodeLabel(row.product_barcode_id),
          ledgerName(row.sales_ledger_id),
          row.margin_percent,
          row.proportion_factor,
          row.discount_percent,
          row.notes,
        ]
          .filter(Boolean)
          .join(' ')
      ),
    [filterByConfigSearch, ledgers, productBarcodes, productPackLedgers]
  );

  const selectedPackBarcode = useMemo(
    () =>
      productBarcodes.find(
        (item) => String(item.id) === String(packLedgerForm.product_barcode_id)
      ) || null,
    [packLedgerForm.product_barcode_id, productBarcodes]
  );

  const firstPositiveNumber = (...values) => {
    for (const value of values) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }

    return 0;
  };

  const previewInventory = useMemo(() => findPurchasePreviewInventory(
    purchaseInventory, packLedgerForm.product_barcode_id, packLedgerForm.warehouse_id
  ), [purchaseInventory, packLedgerForm.product_barcode_id, packLedgerForm.warehouse_id]);

  const defaultPreviewBaseAmount = useMemo(
    () =>
      firstPositiveNumber(
        previewInventory?.unit_price,
        selectedPackBarcode?.unit_price,
        selectedPackBarcode?.purchase_price,
        selectedPackBarcode?.expected_unit_price,
        selectedPackBarcode?.actual_unit_price,
        selectedPackBarcode?.package_amount,
        selectedPackBarcode?.dprice,
        selectedPackBarcode?.price
      ),
    [selectedPackBarcode, previewInventory]
  );

  useEffect(() => {
    if (defaultPreviewBaseAmount > 0 && !previewBaseAmount) {
      setPreviewBaseAmount(String(defaultPreviewBaseAmount));
    }
  }, [defaultPreviewBaseAmount, previewBaseAmount]);

  const previewAmount = useMemo(() => {
    const baseAmount = firstPositiveNumber(
      previewBaseAmount,
      defaultPreviewBaseAmount
    );
    const margin = Number(packLedgerForm.margin_percent || 0);
    const factor = Number(packLedgerForm.proportion_factor || 1);
    return calculatePurchaseMarginPreview({
      baseAmount, margin, factor, inventory: previewInventory,
    });
  }, [defaultPreviewBaseAmount, packLedgerForm.margin_percent, packLedgerForm.proportion_factor, previewBaseAmount, previewInventory]);

  const autoDiscount = previewAmount.mrp !== null && Boolean(
    String(selectedPackBarcode?.barcode || selectedPackBarcode?.vendor_barcode || previewInventory?.vendor_barcode || '').trim()
  );
  const calculatedDiscount = previewAmount.effectiveDiscount === null
    ? 0 : Math.max(0, Math.ceil(previewAmount.effectiveDiscount));

  const packSuggestions = useMemo(() => {
    const queryText = packSearch.trim().toLowerCase();
    if (!queryText) return [];
    const wildcardSuggestions =
      '*'.includes(queryText) || 'all products'.includes(queryText)
        ? [{ id: '*', label: '*', searchHint: 'Default for all unconfigured products' }]
        : [];
    return [
      ...wildcardSuggestions,
      ...productBarcodes
      .filter((row) => barcodeSearchText(row).includes(queryText))
      .slice(0, 20)
      .map((row) => ({
        ...row,
        label: makeBarcodeLabel(row),
        searchHint: [
          getBrandName(row) ? `Brand: ${getBrandName(row)}` : '',
          row.barcode ? `Vendor: ${row.barcode}` : '',
          row.hsncode ? `HSN: ${row.hsncode}` : '',
        ].filter(Boolean).join(' | '),
      })),
    ];
  }, [packSearch, productBarcodes]);

  const resetPackLedgerForm = () => {
    if (location.state?.editPackLedgerId) {
      const { editPackLedgerId, ...remainingState } = location.state;
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: remainingState,
      });
    }
    setPackLedgerForm(emptyPackLedger);
    setEditingPackLedgerId(null);
    setPackSearch('');
    setPreviewBaseAmount('');
  };

  const editPackLedger = useCallback((row) => {
    setEditingPackLedgerId(row.id);
    setPackLedgerForm({
      product_barcode_id: row.product_barcode_id ?? '*',
      destination_type: row.destination_type || 'all',
      dispatch_to_id: row.dispatch_to_id || '',
      warehouse_id: row.warehouse_id || '',
      sales_ledger_id: row.sales_ledger_id || '',
      margin_percent: row.margin_percent ?? 0,
      proportion_factor: row.proportion_factor ?? 1,
      discount_percent: row.discount_percent ?? 0,
      is_active: row.is_active ?? true,
      notes: row.notes || '',
    });
    setPackSearch(barcodeLabel(row.product_barcode_id));
    setMessage('');
    setError('');
  }, [barcodeLabel]);

  useEffect(() => {
    if (activeView !== 'product-pack-margins') return;

    const editPackLedgerId = location.state?.editPackLedgerId;
    if (!editPackLedgerId || editingPackLedgerId) return;

    const row = productPackLedgers.find(
      (item) => String(item.id) === String(editPackLedgerId)
    );
    if (row) {
      editPackLedger(row);
    }
  }, [
    activeView,
    editPackLedger,
    editingPackLedgerId,
    location.state,
    productPackLedgers,
  ]);

  const handleEditPackLedger = (row) => {
    if (activeView !== 'product-pack-margins') {
      navigate('/ecosystem/catalog/cost-ledger-config/product-pack-margins', {
        state: { editPackLedgerId: row.id },
      });
      return;
    }

    editPackLedger(row);
  };

  const savePackLedgerForm = async (event) => {
    event.preventDefault();
    if (!packLedgerForm.product_barcode_id) {
      setError('Select a product pack from the suggestions before saving');
      setMessage('');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      let savedRow;
      const payload = { ...packLedgerForm,
        discount_percent: autoDiscount ? calculatedDiscount : packLedgerForm.discount_percent };
      if (editingPackLedgerId) {
        const response = await axios.put(
          apiUrl(`/accounts/product-pack-ledgers/${editingPackLedgerId}`),
          payload,
          authConfig
        );
        savedRow = response.data;
        setMessage('Product pack margin updated');
      } else {
        const response = await axios.post(
          apiUrl('/accounts/product-pack-ledgers'),
          payload,
          authConfig
        );
        savedRow = response.data;
        setMessage('Product pack margin saved');
      }
      if (savedRow?.id) {
        setProductPackLedgers((rows) => {
          const existingIndex = rows.findIndex(
            (row) => String(row.id) === String(savedRow.id)
          );
          if (existingIndex === -1) return [savedRow, ...rows];
          return rows.map((row, index) =>
            index === existingIndex ? savedRow : row
          );
        });
      }
      resetPackLedgerForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save product pack margin');
    } finally {
      setSaving(false);
    }
  };

  const saveBulkMarginRow = async ({ payload, existingId }) => {
    const response = existingId
      ? await axios.put(apiUrl(`/accounts/product-pack-ledgers/${existingId}`), payload, authConfig)
      : await axios.post(apiUrl('/accounts/product-pack-ledgers'), payload, authConfig);
    const savedRow = response.data;
    if (savedRow?.id) setProductPackLedgers(rows => {
      const exists = rows.some(row => String(row.id) === String(savedRow.id));
      return exists ? rows.map(row => String(row.id) === String(savedRow.id) ? savedRow : row) : [savedRow, ...rows];
    });
  };

  const initialLoading = loading && !loadedOnce;

  return (
    <section className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {selectedView.title}
            </h2>
            <p className="text-sm text-gray-500">
              {selectedView.description}
            </p>
          </div>
          <button
            type="button"
            onClick={loadAll}
            disabled={loading || saving}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading' : 'Refresh'}
          </button>
        </div>
      </div>

      {message ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
          {message}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      ) : null}

      <section className="flex gap-2 overflow-x-auto pb-1">
        {configViews.map((view) => (
          <NavLink
            key={view.key}
            to={view.to}
            end={view.key === 'overview'}
            className={({ isActive }) =>
              `whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'border bg-white text-gray-700 hover:bg-gray-50'
              }`
            }
          >
            {view.label}
          </NavLink>
        ))}
      </section>

      {(showOverview || selectedView.key === 'market-changes') && <MarketChangesConfigSection token={token} productBarcodes={productBarcodes} warehouses={warehouses} outlets={outlets} />}

      <section className={`relative rounded-lg border bg-white p-3 shadow-sm ${selectedView.key === 'market-changes' ? 'hidden' : ''}`}>
        <div className="flex items-center gap-2">
          <Search size={18} className="text-gray-400" />
          <input
            type="text"
            value={configSearch}
            onFocus={() => setConfigSearchOpen(true)}
            onChange={(event) => {
              setConfigSearch(event.target.value);
              setConfigSearchOpen(true);
            }}
            placeholder={searchPlaceholder}
            className="h-10 flex-1 rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          {configSearch ? (
            <button
              type="button"
              onClick={() => {
                setConfigSearch('');
                setConfigSearchOpen(false);
              }}
              className="inline-flex h-10 items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <X size={16} />
              Clear
            </button>
          ) : null}
        </div>

        {configSearchOpen && configSearchText ? (
          <div className="absolute left-3 right-3 z-30 mt-2 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {configSearchSuggestions.length ? (
              configSearchSuggestions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setConfigSearch(item.value);
                    setConfigSearchOpen(false);
                  }}
                  className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-blue-50"
                >
                  <span className="block font-semibold text-gray-900">
                    {item.label}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {item.type}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500">
                No matching configuration
              </div>
            )}
          </div>
        ) : null}
      </section>

      {initialLoading ? (
        <section className="rounded-xl border bg-white p-10 text-center shadow-sm">
          <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
          <div className="text-sm font-semibold text-gray-800">
            Loading Cost & Ledger Config
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Fetching ledgers, cost heads, allocation rules, and pack margins
          </div>
        </section>
      ) : (
      <>

      {showProductPackMarginForm && <BulkMarginImport
        references={{ productBarcodes, configurations: productPackLedgers, warehouses, outlets, stakeholders, ledgers }}
        onSaveRow={saveBulkMarginRow} disabled={saving || loading} productLabel={barcodeLabel} onBusyChange={setSaving} />}

      <section
        className={
          showOverview
            ? 'space-y-4'
            : 'grid gap-4 lg:grid-cols-[420px_minmax(0,1fr)]'
        }
      >
      {hasConfigForm ? (
      <section className="space-y-4">
        {showLedgerForm ? (
          <form
          onSubmit={(event) =>
            saveForm(event, '/accounts/ledgers', ledgerForm, () => setLedgerForm(emptyLedger), 'Ledger saved')
          }
          className="rounded-lg border bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900">
            <Plus size={18} />
            Ledger
          </div>
          <div className="space-y-3">
            <FormInput label="Code" value={ledgerForm.ledger_code} onChange={(e) => setLedgerForm((form) => ({ ...form, ledger_code: e.target.value }))} required />
            <FormInput label="Name" value={ledgerForm.ledger_name} onChange={(e) => setLedgerForm((form) => ({ ...form, ledger_name: e.target.value }))} required />
            <FormSelect label="Type" value={ledgerForm.ledger_type} onChange={(e) => setLedgerForm((form) => ({ ...form, ledger_type: e.target.value }))}>
              {ledgerTypes.map((type) => <option key={type} value={type}>{type}</option>)}
            </FormSelect>
            <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-800 px-4 text-sm font-bold text-white hover:bg-slate-900 disabled:bg-gray-400">
              <Save size={16} />
              Save Ledger
            </button>
          </div>
        </form>
        ) : null}

        {showCostHeadForm ? (
          <form
          onSubmit={(event) =>
            saveForm(event, '/accounts/cost-heads', costHeadForm, () => setCostHeadForm(emptyCostHead), 'Cost head saved')
          }
          className="rounded-lg border bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900">
            <Plus size={18} />
            Cost Head
          </div>
          <div className="space-y-3">
            <FormInput label="Code" value={costHeadForm.cost_head_code} onChange={(e) => setCostHeadForm((form) => ({ ...form, cost_head_code: e.target.value }))} required />
            <FormInput label="Name" value={costHeadForm.cost_head_name} onChange={(e) => setCostHeadForm((form) => ({ ...form, cost_head_name: e.target.value }))} required />
            <FormSelect label="Stage" value={costHeadForm.stage} onChange={(e) => setCostHeadForm((form) => ({ ...form, stage: e.target.value }))}>
              {stages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
            </FormSelect>
            <FormSelect label="Ledger" value={costHeadForm.ledger_id} onChange={(e) => setCostHeadForm((form) => ({ ...form, ledger_id: e.target.value }))}>
              <option value="">Select ledger</option>
              {ledgers.map((ledger) => <option key={ledger.id} value={ledger.id}>{ledger.ledger_name}</option>)}
            </FormSelect>
            <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-800 px-4 text-sm font-bold text-white hover:bg-slate-900 disabled:bg-gray-400">
              <Save size={16} />
              Save Cost Head
            </button>
          </div>
        </form>
        ) : null}

        {showProductPackMarginForm ? (
          <form
          onSubmit={savePackLedgerForm}
          className="rounded-lg border bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900">
            {editingPackLedgerId ? <Pencil size={18} /> : <Plus size={18} />}
            <span className="flex-1">{editingPackLedgerId ? 'Edit Product Pack Margin' : 'New Product Pack Margin'}</span>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                resetPackLedgerForm();
                setMessage('');
                setError('');
              }}
              className="rounded-lg bg-emerald-700 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              + Add New Margin
            </button>
          </div>
          <div className="space-y-3">
            <ProductPackSearch
              label="Product Pack"
              value={packLedgerForm.product_barcode_id}
              search={packSearch}
              suggestions={packSuggestions}
              selectedLabel={barcodeLabel(packLedgerForm.product_barcode_id)}
              onSearch={(nextValue) => {
                setPackSearch(nextValue);
                setPackLedgerForm((form) => ({ ...form, product_barcode_id: '' }));
              }}
              onSelect={(item) => {
                setPackLedgerForm((form) => ({ ...form, product_barcode_id: item.id }));
                setPackSearch(item.label);
                const unitPrice = firstPositiveNumber(
                  item.unit_price,
                  item.purchase_price,
                  item.expected_unit_price,
                  item.actual_unit_price,
                  item.package_amount,
                  item.dprice,
                  item.price
                );
                setPreviewBaseAmount(unitPrice > 0 ? String(unitPrice) : '');
              }}
            />
            <FormSelect
              label="Dispatch To"
              value={packLedgerForm.destination_type}
              onChange={(e) => setPackLedgerForm((form) => ({
                ...form,
                destination_type: e.target.value,
                dispatch_to_id: '',
              }))}
            >
              <option value="all">All destinations</option>
              <option value="outlet">Outlet</option>
              <option value="internal_packing">Internal Packing Dept</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
              <option value="warehouse">Warehouse</option>
            </FormSelect>
            <FormSelect
              label="Destination"
              value={packLedgerForm.dispatch_to_id}
              onChange={(e) => setPackLedgerForm((form) => ({
                ...form,
                dispatch_to_id: e.target.value,
              }))}
              disabled={packLedgerForm.destination_type === 'all'}
            >
              <option value="">
                {packLedgerForm.destination_type === 'internal_packing'
                  ? 'Internal Packing Dept'
                  : `All ${packLedgerForm.destination_type === 'all' ? 'destinations' : packLedgerForm.destination_type}`}
              </option>
              {marginDestinations
                .filter((row) => row.id !== '')
                .map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
            </FormSelect>
            <FormSelect
              label="Warehouse"
              value={packLedgerForm.warehouse_id}
              onChange={(e) => setPackLedgerForm((form) => ({
                ...form,
                warehouse_id: e.target.value,
              }))}
            >
              <option value="">All warehouses</option>
              {warehouses.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.warehouse_name || row.warehouse_code || row.id}
                </option>
              ))}
            </FormSelect>
            <FormInput
              label="Margin %"
              type="number"
              min="0"
              step="0.001"
              value={packLedgerForm.margin_percent}
              onChange={(e) => setPackLedgerForm((form) => ({ ...form, margin_percent: e.target.value }))}
            />
            <FormInput
              label="Proportion Factor"
              type="number"
              min="0"
              step="0.001"
              value={packLedgerForm.proportion_factor}
              onChange={(e) => setPackLedgerForm((form) => ({ ...form, proportion_factor: e.target.value }))}
            />
            <FormInput
              label="Discount %"
              type="number"
              min="0"
              max="100"
              step={autoDiscount ? '1' : '0.001'}
              readOnly={autoDiscount}
              value={autoDiscount ? calculatedDiscount : packLedgerForm.discount_percent}
              onChange={(e) => setPackLedgerForm((form) => ({ ...form, discount_percent: e.target.value }))}
            />
            {autoDiscount && <p className="text-xs text-slate-500">Discount updates automatically with margin and rounds up to a whole percent (2.1% becomes 3%). If D.Price exceeds MRP, discount is 0% and a warning appears below.</p>}
            <FormInput
              label="Preview Base Amount"
              type="number"
              min="0"
              step="0.01"
              value={previewBaseAmount}
              onChange={(e) => setPreviewBaseAmount(e.target.value)}
              placeholder={
                defaultPreviewBaseAmount > 0
                  ? `Default: ${defaultPreviewBaseAmount.toFixed(2)}`
                  : 'Example: 165'
              }
            />
            {defaultPreviewBaseAmount > 0 ? (
              <div className="text-xs font-semibold text-slate-500">
                Default base amount from latest inventory unit price: ₹
                {defaultPreviewBaseAmount.toFixed(2)}
              </div>
            ) : null}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="mb-2 font-bold text-slate-800">Amount Preview</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <span className="text-slate-500">Base amount:</span>{' '}
                  <span className="font-semibold text-slate-900">
                    ₹{previewAmount.baseAmount.toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Effective margin:</span>{' '}
                  <span className="font-semibold text-slate-900">
                    {previewAmount.effectiveMargin.toFixed(3)}%
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Purchase verified MRP:</span>{' '}
                  <span className="font-semibold text-slate-900">
                    {previewAmount.mrp === null
                      ? !packLedgerForm.product_barcode_id || packLedgerForm.product_barcode_id === '*'
                        ? 'Select a specific product pack'
                        : !previewInventory
                          ? 'No purchase inventory for this pack'
                          : 'MRP not saved on this inventory record'
                      : `₹${previewAmount.mrp.toFixed(2)}`}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">D.Price calc:</span>{' '}
                  <span className={previewAmount.mrp !== null && previewAmount.dpriceBeforeRound > previewAmount.mrp ? 'rounded bg-red-100 px-1 font-bold text-red-700' : 'font-semibold text-slate-900'}>
                    ₹{previewAmount.dpriceBeforeRound.toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Invoice D.Price:</span>{' '}
                  <span className={previewAmount.exceedsMrp ? 'rounded bg-red-100 px-1 font-bold text-red-700' : 'font-semibold text-slate-900'}>
                    ₹{previewAmount.dprice.toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Effective discount:</span>{' '}
                  <span className="font-semibold text-slate-900">
                    {previewAmount.effectiveDiscount === null ? 'Unavailable' : `${previewAmount.effectiveDiscount.toFixed(2)}%`}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">MRP comes from the latest purchase inventory for this pack{packLedgerForm.warehouse_id ? ' in the selected warehouse' : ''}. Margin and discount settings do not change it.</p>
              {selectedPackBarcode && !previewInventory && <p className="mt-2 rounded bg-amber-50 p-2 text-amber-800">No matching inventory was found for MK {selectedPackBarcode.mk_barcode || selectedPackBarcode.id}{packLedgerForm.warehouse_id ? ' in the selected warehouse' : ''}. Check that the selected MK barcode matches the pack used during purchase verification.</p>}
              {previewAmount.exceedsMrp && <p role="alert" className="mt-2 rounded bg-red-100 p-2 font-bold text-red-700">Invoice D.Price exceeds purchase verified MRP by ₹{(previewAmount.dprice - previewAmount.mrp).toFixed(2)}.</p>}
            </div>
            <FormSelect label="Sales Ledger" value={packLedgerForm.sales_ledger_id} onChange={(e) => setPackLedgerForm((form) => ({ ...form, sales_ledger_id: e.target.value }))}>
              <option value="">Select ledger</option>
              {ledgers.map((ledger) => <option key={ledger.id} value={ledger.id}>{ledger.ledger_name}</option>)}
            </FormSelect>
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-800 px-4 text-sm font-bold text-white hover:bg-slate-900 disabled:bg-gray-400">
                {saving ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <Save size={16} />
                )}
                {saving
                  ? editingPackLedgerId
                    ? 'Updating Pack Margin'
                    : 'Saving Pack Margin'
                  : editingPackLedgerId
                    ? 'Update Pack Margin'
                    : 'Save Pack Margin'}
              </button>
              {editingPackLedgerId ? (
                <button
                  type="button"
                  onClick={resetPackLedgerForm}
                  disabled={saving}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-50"
                >
                  <X size={16} />
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        </form>
        ) : null}

        {showAllocationRuleForm ? (
          <form
          onSubmit={(event) =>
            saveForm(event, '/accounts/allocation-rules', ruleForm, () => setRuleForm(emptyAllocationRule), 'Allocation rule saved')
          }
          className="rounded-lg border bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900">
            <Plus size={18} />
            Allocation Rule
          </div>
          <div className="space-y-3">
            <FormSelect label="Cost Head" value={ruleForm.cost_head_id} onChange={(e) => setRuleForm((form) => ({ ...form, cost_head_id: e.target.value }))} required>
              <option value="">Select cost head</option>
              {costHeads.map((head) => <option key={head.id} value={head.id}>{head.cost_head_name}</option>)}
            </FormSelect>
            <FormSelect label="Allocation" value={ruleForm.allocation_method} onChange={(e) => setRuleForm((form) => ({ ...form, allocation_method: e.target.value }))}>
              {allocationMethods.map((method) => <option key={method} value={method}>{method}</option>)}
            </FormSelect>
            <FormInput label="Apply Level" value={ruleForm.apply_level} onChange={(e) => setRuleForm((form) => ({ ...form, apply_level: e.target.value }))} />
            <FormInput label="Priority" type="number" value={ruleForm.priority} onChange={(e) => setRuleForm((form) => ({ ...form, priority: e.target.value }))} />
            <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-800 px-4 text-sm font-bold text-white hover:bg-slate-900 disabled:bg-gray-400">
              <Save size={16} />
              Save Rule
            </button>
          </div>
        </form>
        ) : null}
      </section>
      ) : null}

      <section className={showOverview ? 'grid gap-4 xl:grid-cols-2' : 'grid gap-4'}>
        {showLedgersTable ? (
          <ConfigTable title="Ledgers" rows={filteredLedgers} emptyText={configSearchText ? 'No matching ledgers' : 'No ledgers configured'} columns={[{ key: 'id', label: 'Ledger ID' }, { key: 'ledger_code', label: 'Code' }, { key: 'ledger_name', label: 'Name' }, { key: 'ledger_type', label: 'Type' }]} />
        ) : null}
        {showCostHeadsTable ? (
          <ConfigTable title="Cost Heads" rows={filteredCostHeads} emptyText={configSearchText ? 'No matching cost heads' : 'No cost heads configured'} columns={[{ key: 'cost_head_code', label: 'Code' }, { key: 'cost_head_name', label: 'Name' }, { key: 'stage', label: 'Stage' }, { key: 'ledger_id', label: 'Ledger', render: (row) => ledgerName(row.ledger_id) }]} />
        ) : null}
        {showAllocationRulesTable ? (
          <ConfigTable title="Allocation Rules" rows={filteredAllocationRules} emptyText={configSearchText ? 'No matching allocation rules' : 'No allocation rules configured'} columns={[{ key: 'cost_head_id', label: 'Cost Head', render: (row) => costHeadName(row.cost_head_id) }, { key: 'allocation_method', label: 'Method' }, { key: 'apply_level', label: 'Level' }, { key: 'priority', label: 'Priority' }]} />
        ) : null}
        {showProductPackMarginsTable ? (
          <ConfigTable title="Product Pack Margin Config" rows={filteredProductPackLedgers} emptyText={configSearchText ? 'No matching product pack margins' : 'No product pack margin rows yet'} columns={[
          { key: 'product_barcode_id', label: 'Product Pack', render: (row) => barcodeLabel(row.product_barcode_id) },
          { key: 'destination_type', label: 'Dispatch To', render: (row) => row.destination_type === 'all' || !row.destination_type ? 'All' : row.destination_type.replaceAll('_', ' ') },
          { key: 'dispatch_to_id', label: 'Destination', render: destinationName },
          { key: 'warehouse_id', label: 'Warehouse', render: (row) => warehouses.find((item) => String(item.id) === String(row.warehouse_id))?.warehouse_name || (row.warehouse_id ? `#${row.warehouse_id}` : 'All') },
          { key: 'margin_percent', label: 'Margin %', render: (row) => `${Number(row.margin_percent || 0).toFixed(3)}%` },
          { key: 'proportion_factor', label: 'Factor', render: (row) => Number(row.proportion_factor || 1).toFixed(3) },
          { key: 'effective_margin', label: 'Effective %', render: (row) => `${(Number(row.margin_percent || 0) * Number(row.proportion_factor || 1)).toFixed(3)}%` },
          { key: 'discount_percent', label: 'Discount %', render: (row) => `${Number(row.discount_percent || 0).toFixed(3)}%` },
          { key: 'sales_ledger_id', label: 'Sales Ledger', render: (row) => ledgerName(row.sales_ledger_id) },
          {
            key: 'actions',
            label: 'Actions',
            render: (row) => (
              <button
                type="button"
                onClick={() => handleEditPackLedger(row)}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 hover:bg-blue-100"
              >
                <Pencil size={14} />
                Edit
              </button>
            ),
          },
        ]} busy={saving && showProductPackMarginForm} />
        ) : null}
      </section>
      </section>
        </>
      )}
    </section>
  );
};

export default CostLedgerConfigSection;
